// src/pages/player/PlayerDashboard.jsx
import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import API from '../../api/api';
import { FaUserEdit, FaCalendarPlus, FaAward, FaListAlt, FaSpinner, FaTrophy } from 'react-icons/fa';

const sportThemes = {
    default: {
        gradient: "from-slate-800 to-indigo-900",
        accent: "text-indigo-700 bg-indigo-50 border-indigo-200",
        badge: "bg-indigo-500/20 border-indigo-400/30 text-indigo-200",
        button: "bg-indigo-600 hover:bg-indigo-700",
        cardBg: "group-hover:bg-indigo-50",
        bannerTitle: "Para Sports Association",
        bgLight: "bg-slate-50",
        textGradient: "from-indigo-600 to-teal-600",
        quote: "Strength does not come from physical capacity. It comes from an indomitable will."
    },
    athletics: {
        gradient: "from-orange-600 to-red-800",
        accent: "text-orange-700 bg-orange-50 border-orange-200",
        badge: "bg-orange-500/20 border-orange-400/30 text-orange-200",
        button: "bg-orange-600 hover:bg-orange-700",
        cardBg: "group-hover:bg-orange-50",
        bannerTitle: "Track & Field Championship Mode",
        bgLight: "bg-orange-50/10",
        textGradient: "from-orange-600 to-red-600",
        quote: "Run when you can, walk if you must, crawl if you must; just never give up."
    },
    swimming: {
        gradient: "from-cyan-500 to-blue-800",
        accent: "text-cyan-700 bg-cyan-50 border-cyan-200",
        badge: "bg-cyan-500/20 border-cyan-400/30 text-cyan-200",
        button: "bg-cyan-600 hover:bg-cyan-700",
        cardBg: "group-hover:bg-cyan-50",
        bannerTitle: "Para Aquatics Mode",
        bgLight: "bg-cyan-50/10",
        textGradient: "from-cyan-500 to-blue-600",
        quote: "Water is the ultimate equalizer. Dive in and make your own waves."
    },
    badminton: {
        gradient: "from-emerald-500 to-teal-800",
        accent: "text-emerald-700 bg-emerald-50 border-emerald-200",
        badge: "bg-emerald-500/20 border-emerald-400/30 text-emerald-200",
        button: "bg-emerald-600 hover:bg-emerald-700",
        cardBg: "group-hover:bg-emerald-50",
        bannerTitle: "Para Badminton Mode",
        bgLight: "bg-emerald-50/10",
        textGradient: "from-emerald-500 to-teal-600",
        quote: "Smash through your limitations on and off the court."
    },
    powerlifting: {
        gradient: "from-amber-500 to-yellow-800",
        accent: "text-amber-700 bg-amber-50 border-amber-200",
        badge: "bg-amber-500/20 border-amber-400/30 text-amber-200",
        button: "bg-amber-600 hover:bg-amber-700",
        cardBg: "group-hover:bg-amber-50",
        bannerTitle: "Powerlifting Heavyweight Mode",
        bgLight: "bg-amber-50/10",
        textGradient: "from-amber-600 to-yellow-600",
        quote: "The resistance you fight physically and in life can only build a strong character."
    }
};

const StatCard = ({ count, label, icon, loading, theme }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border flex items-center gap-4">
        <div className={`p-3 rounded-full ${theme.accent}`}>
            {icon}
        </div>
        <div>
            {loading ? (
                <div className="flex items-center text-slate-400">
                    <FaSpinner className="animate-spin text-xl" />
                </div>
            ) : (
                <p className="text-3xl font-bold text-slate-800">{count}</p>
            )}
            <p className="text-sm text-slate-500">{label}</p>
        </div>
    </div>
);

const ActionCard = ({ to, title, description, icon, theme }) => (
    <Link 
        to={to} 
        className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-md hover:-translate-y-0.5 transition-all group"
    >
        <div className={`mb-3 w-fit p-3 rounded-full transition-colors ${theme.accent} ${theme.cardBg}`}>
            {icon}
        </div>
        <h3 className="text-lg font-bold text-slate-800 mb-1">{title}</h3>
        <p className="text-xs text-slate-500 group-hover:text-slate-700 transition-colors leading-relaxed">{description}</p>
        <span className="font-semibold text-xs mt-4 inline-block opacity-0 group-hover:opacity-100 transition-all text-slate-700">
            Manage &rarr;
        </span>
    </Link>
);

const PlayerDashboard = () => {
    const { user } = useContext(AuthContext);
    const [selectedTheme, setSelectedTheme] = useState("default");
    const [stats, setStats] = useState({
        registeredEvents: 0,
        certificates: 0
    });
    const [classifications, setClassifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const activeTheme = sportThemes[selectedTheme] || sportThemes.default;

    useEffect(() => {
        if (!user?.id) return;
        
        setLoading(true);
        Promise.all([
            API.fetchPlayerRegistrations(user.id),
            API.fetchPlayerCertificates(),
            API.fetchMyClassifications()
        ]).then(([regRes, certRes, classRes]) => {
            const regs = regRes?.data || regRes || [];
            const certs = certRes?.data || certRes || [];
            const classes = classRes?.data || classRes || [];
            setStats({
                registeredEvents: regs.length,
                certificates: certs.length
            });
            setClassifications(classes);
        }).catch((err) => {
            console.error("Error loading player dashboard stats:", err);
        }).finally(() => {
            setLoading(false);
        });
    }, [user]);

    return (
        <div className="space-y-6">
            
            {/* Sport Theme Selector */}
            <div className="flex flex-wrap gap-2 items-center bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider px-2">Sport theme:</span>
                {Object.keys(sportThemes).map((t) => (
                    <button
                        key={t}
                        onClick={() => setSelectedTheme(t)}
                        className={`px-3.5 py-1.5 rounded-full text-xs font-bold border capitalize transition-all ${
                            selectedTheme === t
                                ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                                : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                        }`}
                    >
                        {t === "default" ? "All Sports" : t}
                    </button>
                ))}
            </div>

            {/* Profile Hero Card Banner */}
            <div className={`bg-gradient-to-r ${activeTheme.gradient} rounded-3xl p-6 sm:p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden transition-all duration-500`}>
                <div className="absolute right-0 top-0 opacity-10 pointer-events-none transform translate-x-12 -translate-y-6">
                    <FaTrophy size={200} />
                </div>
                
                <div className="space-y-4 text-center md:text-left z-10 max-w-xl">
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border uppercase tracking-widest ${activeTheme.badge}`}>
                        {activeTheme.bannerTitle}
                    </span>
                    <h1 className="text-3xl sm:text-4xl font-black tracking-tight">
                        Welcome back, {user?.name || 'Player'}!
                    </h1>
                    <p className="text-xs sm:text-sm text-slate-200 font-medium italic">
                        "{activeTheme.quote}"
                    </p>
                </div>

                {/* Player details summary */}
                <div className="w-full md:w-auto bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10 space-y-2 z-10 text-xs text-white/90">
                    <div>
                        <span className="font-bold text-white/60 mr-1.5">District:</span>
                        <span className="font-semibold">{user?.district || "N/A"}</span>
                    </div>
                    <div>
                        <span className="font-bold text-white/60 mr-1.5">Gender:</span>
                        <span className="font-semibold">{user?.gender || "N/A"}</span>
                    </div>
                    <div>
                        <span className="font-bold text-white/60 mr-1.5">Date of Birth:</span>
                        <span className="font-semibold">{user?.dob || "N/A"}</span>
                    </div>
                    <div>
                        <span className="font-bold text-white/60 mr-1.5">Classification Status:</span>
                        <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/20 font-bold">
                            Active
                        </span>
                    </div>
                </div>
            </div>

            {/* Mandatory District Intimation Reminder */}
            <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-2 border-amber-300 rounded-2xl p-4 sm:p-5 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                    <span className="text-2xl mt-0.5">⚠️</span>
                    <div>
                        <h2 className="text-sm font-bold text-amber-950">
                            Mandatory District Intimation for Event Registrations
                        </h2>
                        <p className="text-xs text-amber-900 mt-0.5 leading-relaxed">
                            Prior intimation to your corresponding District Secretary or President is mandatory before applying for any event/game. District authorization is required as district officials must approve your entries.
                        </p>
                    </div>
                </div>
                <Link
                    to="/player/district-contacts"
                    className="shrink-0 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs rounded-xl shadow-xs transition flex items-center gap-1.5"
                >
                    View District Contacts &rarr;
                </Link>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                    count={stats.registeredEvents} 
                    label="Registered Events" 
                    icon={<FaListAlt size={22} />} 
                    loading={loading}
                    theme={activeTheme}
                />
                <StatCard 
                    count={stats.certificates} 
                    label="Certificates Won" 
                    icon={<FaAward size={22} />} 
                    loading={loading}
                    theme={activeTheme}
                />
            </div>

            {/* Classifications Section */}
            {classifications.length > 0 && (
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <FaTrophy className="text-yellow-500" />
                        My Sport Classifications
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {classifications.map((item, idx) => (
                            <div key={idx} className="p-4 rounded-xl border border-slate-100 bg-slate-50/50 flex flex-col gap-1">
                                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Sport</span>
                                <span className="text-base font-bold text-slate-800">{item.sport_name}</span>
                                <div className="mt-2 flex items-center justify-between">
                                    <div>
                                        <span className="text-xs text-slate-400 block">Class Code</span>
                                        <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 border border-indigo-200 text-indigo-700 uppercase">
                                            {item.classification_code}
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-xs text-slate-400 block">Discipline</span>
                                        <span className="text-xs font-bold text-slate-600 capitalize">
                                            {item.discipline || "General"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Action Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <ActionCard
                    to="/player-profile"
                    title="My Profile"
                    description="View and update your personal information, contact info, and uploaded documents."
                    icon={<FaUserEdit size={28}/>}
                    theme={activeTheme}
                />
                <ActionCard
                    to="/register-event"
                    title="Register for an Event"
                    description="Browse all open events and register for new competitions."
                    icon={<FaCalendarPlus size={28}/>}
                    theme={activeTheme}
                />
                <ActionCard
                    to="/certificates"
                    title="My Certificates"
                    description="View and download all your official participation and achievement certificates."
                    icon={<FaAward size={28}/>}
                    theme={activeTheme}
                />
                <ActionCard
                    to="/player/my-achievements"
                    title="Past Achievements"
                    description="Add and manage your medals, tournament records, and certificates for admin verification."
                    icon={<FaTrophy size={28}/>}
                    theme={activeTheme}
                />
            </div>
        </div>
    );
};

export default PlayerDashboard;
